import { Flex, Stack, Text } from '@mantine/core';
import {
  ManualTriggerInput,
  ManualTriggerInputType,
  IntervalSchedule,
  TriggerStep,
  TriggerType,
} from '@/types/automationSteps.ts';
import { useUpdateStepFields } from '@/hooks/api/steps/useUpdateStepFields.ts';
import StyledSelect from '@/components/StyledInputFields/StyledSelect.tsx';
import * as React from 'react';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import AppIcon from '@/components/AppIcon.tsx';
import { HiOutlinePlus } from 'react-icons/hi2';
import StyledInputLabel from '@/components/StyledInputFields/StyledInputLabel.tsx';
import ManualTriggerInputFields from './ManualTriggerInputFields.tsx';
import { capitalizeFirstLetter, generateUid } from '@/utils/misc.ts';
import IntegrationFieldsContainer from '@/pages/Editor/components/SharedFields/IntegrationFields/IntegrationFieldsContainer.tsx';
import OutputNameField from '@/pages/Editor/components/SharedFields/OutputNameField.tsx';
import { StepErrorType } from '@/types/stepErrorTypes.ts';
import useStepErrors from '@/hooks/useStepErrors.ts';
import { useEditorState } from '@/state/editorState.ts';
import { useHover } from '@mantine/hooks';
import StyledInputErrorText from '@/components/StyledInputFields/StyledInputErrorText.tsx';
import { useEditingVersionSnapshot } from '@/context/EditVersionContext/useEditVersionContext.ts';
import useStepIntegration from '@/hooks/useStepIntegration.ts';
import { StepType } from '@/types/automationFields.ts';
import type { TriggerActionInputPort } from '@/types/integrations.ts';
import ScheduledTriggerFields from './ScheduledTriggerFields.tsx';
import { createDefaultSchedule } from './scheduledTriggerSchedule.ts';

type props = {
  step: TriggerStep;
};

type Fields = {
  trigger_type: TriggerType;
  schedule?: IntervalSchedule | null;
  integration_config: TriggerStep['integration_config'];
  manual_trigger_inputs: ManualTriggerInput[];
};

const providerInputType = (datasetType: string): ManualTriggerInputType | null => {
  switch (datasetType) {
    case 'directory':
      return ManualTriggerInputType.Folder;
    case 'file':
      return ManualTriggerInputType.File;
    case 'json':
      return ManualTriggerInputType.Json;
    default:
      return null;
  }
};

const TriggerFields = ({ step }: props) => {
  const { fields, onChange, onChangeNoDelay } = useUpdateStepFields<Fields>({ step, syncWithAPI: true });
  const latestVersion = useEditingVersionSnapshot();
  const { selectedIntegrationAction, loadingIntegrations } = useStepIntegration(
    StepType.Trigger,
    step.integration_config.integration_id,
    step.integration_config.action_type,
  );
  const declaredProviderBindingIds = new Set(
    selectedIntegrationAction?.secondary_input_ports?.map((port) => port.binding_id) ?? [],
  );
  const matchesProviderPort = (
    input: ManualTriggerInput,
    port: TriggerActionInputPort,
  ) => {
    const expectedInputType = providerInputType(port.dataset_type);
    return (
      input.provider_binding_id === port.binding_id &&
      input.input_type === expectedInputType &&
      input.is_required === port.required
    );
  };
  const unsupportedProviderInputs = loadingIntegrations
    ? []
    : fields.manual_trigger_inputs.filter(
        (input) =>
          !input.provider_binding_id ||
          !declaredProviderBindingIds.has(input.provider_binding_id) ||
          !(selectedIntegrationAction?.secondary_input_ports?.some((port) =>
            matchesProviderPort(input, port),
          ) ?? false),
      );

  const inputErrors = useStepErrors(step).filter(
    (e) => e.type == StepErrorType.InvalidManualTriggerInput,
  );
  const scheduleError = useStepErrors(step).find(
    (error) => error.type === StepErrorType.InvalidSchedule,
  );

  const indexlessError = inputErrors.find((e) => e.index === null);

  const setNewStepId = useEditorState((s) => s.setNewStepId);

  const { hovered, ref: hoverRef } = useHover();

  return (
    <Stack w={'100%'}>
      <StyledSelect
        label="Trigger Type"
        placeholder="Select Trigger Type"
        data={Object.values(TriggerType).map((type) => ({
          value: type,
          label: capitalizeFirstLetter(type),
        }))}
        value={fields.trigger_type}
        onChange={(value) => {
          const triggerType = value as TriggerType;
          onChangeNoDelay({
            trigger_type: triggerType,
            ...(triggerType === TriggerType.Scheduled
              ? {
                  schedule: fields.schedule ?? createDefaultSchedule(),
                  manual_trigger_inputs: [],
                  integration_config: {
                    integration_type: null,
                    integration_id: null,
                    action_type: null,
                    config_values: { fields: [] },
                  },
                }
              : { schedule: null }),
          });
        }}
        required={true}
        allowDeselect={false}
        clearable={false}
      />

      {fields.trigger_type === TriggerType.Scheduled && (
        <ScheduledTriggerFields
          schedule={fields.schedule}
          onSave={(schedule) => onChangeNoDelay({ schedule })}
          serverError={scheduleError?.message}
        />
      )}
      {fields.trigger_type === TriggerType.Manual && (
        <Stack>
          <span>
            <StyledInputLabel>Inputs</StyledInputLabel>
            <Text c={'dimmed'} size={'11px'}>
              Add multiple inputs for your manual run
            </Text>
          </span>

          <Stack
            style={{
              borderColor: indexlessError ? '#f34f52' : '#ECECEC',
              borderStyle: 'solid',
              borderWidth: 1,
              borderRadius: 5,
            }}
            bg={indexlessError ? '#fdeded' : '#F6F6F6'}
            p={fields.manual_trigger_inputs.length > 0 ? 'xs' : 0}
            gap={'0px'}
          >
            {fields.manual_trigger_inputs.map((manualTriggerInput, idx) => (
              <ManualTriggerInputFields
                key={manualTriggerInput.output_id}
                manualTriggerInput={manualTriggerInput}
                onChangeNoDelay={(updatedInput: ManualTriggerInput) => {
                  const updatedInputs = fields.manual_trigger_inputs.map((input) =>
                    input.output_id === updatedInput.output_id ? updatedInput : input,
                  );
                  onChangeNoDelay({ manual_trigger_inputs: updatedInputs });
                }}
                onChangeDelay={(updatedInput: ManualTriggerInput) => {
                  const updatedInputs = fields.manual_trigger_inputs.map((input) =>
                    input.output_id === updatedInput.output_id ? updatedInput : input,
                  );
                  onChange({ manual_trigger_inputs: updatedInputs });
                }}
                onDelete={() => {
                  const updatedInputs = fields.manual_trigger_inputs.filter(
                    (input) => input.output_id !== manualTriggerInput.output_id,
                  );
                  onChangeNoDelay({ manual_trigger_inputs: updatedInputs });
                }}
                stepId={step.step_id}
                error={inputErrors.find((e) => e.index === idx)}
                schemas={latestVersion?.schemas ?? []}
              />
            ))}
            {indexlessError && (
              <StyledInputErrorText mt={0}>{indexlessError?.message}</StyledInputErrorText>
            )}

            <Flex ref={hoverRef}>
              <StyledButton
                onClick={() => {
                  setNewStepId(step.step_id);

                  const newInput: ManualTriggerInput = {
                    output_id: generateUid(),
                    name: '',
                    input_type: ManualTriggerInputType.File,
                    is_required: true,
                    description: '',
                  };
                  onChangeNoDelay({
                    manual_trigger_inputs: [...fields.manual_trigger_inputs, newInput],
                  });
                }}
                variant={'icon'}
                aria-label="Add trigger input"
                w={'100%'}
                style={{ backgroundColor: hovered ? '#E4E4E4' : '#ECECEC', height: '28px' }}
                my={fields.manual_trigger_inputs.length > 0 ? 'xs' : 0}
              >
                <AppIcon Icon={HiOutlinePlus} size={'16px'} iconProps={{ strokeWidth: '2px' }} />
              </StyledButton>
            </Flex>
          </Stack>
        </Stack>
      )}
      {fields.trigger_type === TriggerType.External && (
        <>
          <IntegrationFieldsContainer step={step} />
          <OutputNameField step={step} />
          {((selectedIntegrationAction?.secondary_input_ports?.length ?? 0) > 0 ||
            fields.manual_trigger_inputs.length > 0) && (
            <Stack gap={2}>
              <StyledInputLabel>Provider inputs</StyledInputLabel>
              {selectedIntegrationAction?.secondary_input_ports?.map((port) => {
                const configured = fields.manual_trigger_inputs.find((input) => matchesProviderPort(input, port));
                const inputType = providerInputType(port.dataset_type);
                return (
                  <Flex key={port.binding_id} align="center" justify="space-between">
                    <Text c="dimmed" size="xs">
                      {port.display_name}
                      {configured ? ` · ${configured.name || configured.output_id}` : ' · not enabled'}
                    </Text>
                    <StyledButton
                      variant="subtle"
                      size="sm"
                      disabled={!configured && inputType === null}
                      onClick={() => {
                        if (configured) {
                          onChangeNoDelay({
                            manual_trigger_inputs: fields.manual_trigger_inputs.filter(
                              (input) => input.output_id !== configured.output_id,
                            ),
                          });
                          return;
                        }
                        if (inputType === null) {
                          return;
                        }
                        onChangeNoDelay({
                          manual_trigger_inputs: [
                            ...fields.manual_trigger_inputs,
                            {
                              output_id: generateUid(),
                              name: port.display_name,
                              description: '',
                              input_type: inputType,
                              provider_binding_id: port.binding_id,
                              is_required: port.required,
                            },
                          ],
                        });
                      }}
                    >
                      {configured ? 'Disable' : inputType === null ? 'Unsupported' : 'Enable'}
                    </StyledButton>
                  </Flex>
                );
              })}
              {unsupportedProviderInputs.map((input) => (
                <Flex key={input.output_id} align="center" justify="space-between">
                  <Text c="red" size="xs">
                    Unsupported provider input · {input.name || input.output_id}
                  </Text>
                  <StyledButton
                    variant="delete"
                    size="sm"
                    onClick={() =>
                      onChangeNoDelay({
                        manual_trigger_inputs: fields.manual_trigger_inputs.filter(
                          (candidate) => candidate.output_id !== input.output_id,
                        ),
                      })
                    }
                  >
                    Remove
                  </StyledButton>
                </Flex>
              ))}
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
};

export default TriggerFields;
